import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { landingPagesApi, organizationsApi, programsApi, usersApi } from 'api';
import banner from 'assets/mentiway-banner.svg';
import { Memberships, ProgramType } from 'types/Memberships';
import { Loader } from 'components/_new/Loader';
import { Box, BoxSection } from 'components/_new/Box';
import { StoredKeys } from 'types/Auth';
import { useAuth } from 'contexts/authContext';
import ChangeOrganization from './panel/ChangeOrganization';
import { ProgramFinishedPage } from '../ProgramFinishedPage';

const ChangeOrganizationWrapper = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { currentMembershipsRefetch } = useAuth();

	const goToProgram = (program: ProgramType) => {
		navigate(`/${program.name}`);
	};

	const lastVisitedProgramName = window.localStorage.getItem(StoredKeys.ProgramName) as string | null;

	const preparePrograms = (data: Memberships[]) => {
		return data
			.filter((membership) => membership.programs && membership.programs.length > 0)
			.map(({ programs }) => programs)
			.flat();
	};

	const [ready, setReady] = useState(false);
	const [defaultSelected, setDefaultSelected] = useState<{ organization: string; program: string } | undefined>(
		undefined
	);

	const { mutate: tryJoinToProgram } = useMutation(
		({
			organizationName,
			programName,
		}: {
			organizationName: string;
			programName: string;
			allPrograms: ProgramType[];
		}) => landingPagesApi.joinProgram(organizationName, programName, true),
		{
			onSuccess: (data, { organizationName, programName, allPrograms }) => {
				if (data === true) {
					currentMembershipsRefetch?.();
					setDefaultSelected({ organization: organizationName, program: programName });
					setReady(true);
				} else if (allPrograms.length === 1) {
					goToProgram(allPrograms[0]);
				} else {
					setReady(true);
				}
			},
			onError: (data, { allPrograms }) => {
				if (allPrograms.length === 1) {
					goToProgram(allPrograms[0]);
				} else {
					setReady(true);
				}
			},
		}
	);

	const { data: memberships } = useQuery(['auth', 'memberships'], usersApi.getMemberships, {
		enabled: true,
		onSuccess: (data) => {
			const allPrograms = preparePrograms(data);

			// when last visited program name is not in membership programs
			if (lastVisitedProgramName && !allPrograms.map(({ name }) => name).includes(lastVisitedProgramName)) {
				const [organizationName, programName] = lastVisitedProgramName.split('/');
				tryJoinToProgram({ organizationName, programName, allPrograms });
			} else if (allPrograms.length === 1) {
				goToProgram(allPrograms[0]);
			} else {
				setReady(true);
			}
		},
	});

	const userMemberships = memberships ? preparePrograms(memberships) : [];
	if (!ready) {
		return <Loader />;
	}
	if (ready && userMemberships.length === 0) {
		return <ProgramFinishedPage />;
	}

	return (
		<>
			<Helmet title={t('landingPages.buttons.chooseOrganization')} />
			<div className="flex flex-column align-items-center justify-content-center row-gap-6 pt-8 pb-7">
				<Box variant="white" dropShadow className="w-480-auto">
					<BoxSection className="sm:px-6 py-4 text-center">
						<img src={banner} alt="banner" className="mt-2 mb-6 align-self-center" />
						<ChangeOrganization defaultSelected={defaultSelected} />
					</BoxSection>
				</Box>
			</div>
		</>
	);
};

export default ChangeOrganizationWrapper;
